/**
 * Staff Detail Page (Sprint 5 S5-B-pages)
 *
 * Tenant-scoped FI Staff detail surface mounted at `/admin/staff/:staffId`.
 * Composes the canonical `useStaffMember` hook (FI Staff Lifecycle spec)
 * with the four sensitive-operation surfaces shipped by S5-B-components:
 * `SuspendConfirmDialog`, `OffboardConfirmDialog`, `StepUpAuthModal`, and
 * `EffectivePermissionsDiff`.
 *
 * Sections:
 *   1. Header -- name, email, status pill, key timestamps.
 *   2. Role bindings -- table of `RoleRef` rows held in this tenant.
 *   3. Effective permissions -- current permission strings + ABAC predicate
 *      references (Sprint 4 evaluator consumes both).
 *   4. Audit tail -- lifecycle and role events for this membership,
 *      newest first, from listStaffAuditEvents (StaffAuditTail, GH #2317).
 *   5. Actions -- [Edit Roles] always; [Suspend] on ACTIVE; [Reactivate]
 *      on SUSPENDED; [Reset sign-in credential] on ACTIVE only, when the
 *      tenant is credential-path (`Staff.credentialPathEnabled`);
 *      [Off-board] on ACTIVE or SUSPENDED.
 *
 * Sensitive operations (Suspend / Off-board / credential reset / role
 * grants involving fi_admin) are gated through `StepUpAuthModal`; the issued token is
 * passed via the `stepUpToken` mutation variable, which the hook layer
 * propagates as the `X-Step-Up-Token` header.
 *
 * Sprint 1 last-admin invariant (409 LAST_ADMIN_INVARIANT on the role
 * update path) is surfaced inline through `EffectivePermissionsDiff`'s
 * `serverError` prop -- the diff component owns the canonical operator
 * copy so the parent does not duplicate it.
 *
 * A failed suspend, reactivate, off-board or credential reset renders inside
 * its own dialog, which stays open for a retry, and each mutation is reset
 * when its dialog closes, so no failure outlives its dialog or masks the
 * next one (GH #2307).
 *
 * [Edit Roles] reads the staff record again each time it opens and seeds its
 * role selection from that read, never from the record cached at page load:
 * the save sends the complete role set, so a stale seed would revoke a role
 * granted since (GH #2307, from the #2318 triage).
 *
 * Pure UI; all data flow goes through canonical hooks. No raw fetch.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft as BackIcon,
  Pencil as EditRolesIcon,
  Pause as SuspendIcon,
  Play as ReactivateIcon,
  KeyRound as ResetCredentialIcon,
  UserMinus as OffboardIcon,
} from 'lucide-react';
import {
  Alert,
  Badge,
  BodyText,
  Button,
  Card,
  Dialog,
  Heading,
  type BadgeProps,
} from '@aaimplatform/ui';
import {
  ApiResponseError,
  StepUpRequiredError,
  useOffboardStaff,
  useReactivateStaff,
  useResetStaffCredential,
  useStaffMember,
  useSuspendStaff,
  useUpdateStaffRoles,
  type RoleRef,
  type Staff,
  type TenantUserStatus,
} from '../../hooks/useStaffLifecycle';
import {
  useEffectivePermissionsPreview,
  type PreviewEffectivePermissionsResponse,
} from '../../hooks/useEffectivePermissionsPreview';
import { toAssignableStaffRoles, useRoleCatalog } from '../../hooks/useRoleCatalog';
import {
  StepUpAuthModal,
  getCachedStepUpToken,
} from '../../components/staff/StepUpAuthModal';
import { SuspendConfirmDialog } from '../../components/staff/SuspendConfirmDialog';
import { OffboardConfirmDialog } from '../../components/staff/OffboardConfirmDialog';
import { ResetCredentialConfirmDialog } from '../../components/staff/ResetCredentialConfirmDialog';
import { StaffAuditTail } from '../../components/staff/StaffAuditTail';
import {
  EffectivePermissionsDiff,
  type EffectivePermissionsDiffServerError,
} from '../../components/staff/EffectivePermissionsDiff';

const STATUS_TO_BADGE: Record<
  TenantUserStatus,
  { variant: NonNullable<BadgeProps['variant']>; label: string }
> = {
  ACTIVE: { variant: 'success', label: 'Active' },
  INVITED: { variant: 'warning', label: 'Invited' },
  SUSPENDED: { variant: 'warning', label: 'Suspended' },
  OFFBOARDED: { variant: 'neutral', label: 'Off-boarded' },
  DELETED: { variant: 'error', label: 'Deleted' },
};

function formatDate(value?: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function buildStepUpHash(operation: string, userId: string): string {
  return `${operation}:${userId}`;
}

interface PendingStepUp {
  operation: 'suspend' | 'reactivate' | 'offboard' | 'edit_roles' | 'reset_credential';
  label: string;
  hash: string;
}

/**
 * Sprint 6: map the local pending-step-up `operation` slug onto the
 * canonical `StepUpOperation` from the auth-step-up spec. The local
 * 'edit_roles' label maps to the spec's camelCase 'updateRoles', and
 * 'reset_credential' maps to 'resetCredential'.
 */
function toCanonicalStepUpOperation(
  op: PendingStepUp['operation'],
): 'suspend' | 'reactivate' | 'offboard' | 'updateRoles' | 'resetCredential' {
  if (op === 'suspend') return 'suspend';
  if (op === 'reactivate') return 'reactivate';
  if (op === 'offboard') return 'offboard';
  if (op === 'reset_credential') return 'resetCredential';
  return 'updateRoles';
}

/**
 * Classify a role-update failure into the code + copy the diff panel renders
 * (GH #2461 deliverable 3, sibling sweep).
 *
 * Two things this fixes, both the same shape as the step-up modal's defect:
 *
 * 1. DISCRIMINATE ON THE CODE, NOT THE PROSE. The server emits
 *    `409 LAST_ADMIN_INVARIANT` as a machine-readable `error` field
 *    (`staff-lifecycle.service.ts:1705`), and `ApiResponseError` now carries it.
 *    The prior form tested `/last[_ ]?admin|LAST_ADMIN_INVARIANT/i` against the
 *    MESSAGE, so rewording the server string would silently downgrade the
 *    failure to a generic UPDATE_FAILED -- losing the one explanation that tells
 *    an operator why the change was refused.
 * 2. NEVER RENDER A CLIENT-SIDE THROW. This mutation is step-up gated and goes
 *    through the middleware whose `request.clone()` TypeError once reached an
 *    operator as a verification message. A browser internal must not reach this
 *    panel either.
 *
 * Server prose IS rendered for an HTTP failure here, unlike the mint modal: a
 * 409 last-admin message is written for the operator, while the mint's 4xx
 * strings are developer-facing.
 *
 * Exported for test. No React, no hooks -- the branch it replaces had no
 * coverage at all, which is the condition that let the original half-fix ship.
 */
export function classifyRoleUpdateFailure(err: unknown): {
  code: 'LAST_ADMIN_INVARIANT' | 'UPDATE_FAILED';
  message: string;
} {
  if (err instanceof ApiResponseError) {
    return {
      code: err.errorCode === 'LAST_ADMIN_INVARIANT' ? 'LAST_ADMIN_INVARIANT' : 'UPDATE_FAILED',
      message: err.message,
    };
  }
  return {
    code: 'UPDATE_FAILED',
    message: 'The role update could not be completed. Check your connection and try again.',
  };
}

/**
 * The message a staff lifecycle dialog shows for its failed mutation
 * (GH #2307). An HTTP failure shows the server's own message:
 * `ApiResponseError`, or `StepUpRequiredError` for a refused or expired
 * step-up token. A client-side throw shows fixed copy, never its own text,
 * as `classifyRoleUpdateFailure` does for the role update (GH #2461).
 * Returns undefined when there is no failure.
 *
 * Exported for test.
 */
export function lifecycleFailureMessage(err: unknown): string | undefined {
  if (err === null || err === undefined) return undefined;
  if (err instanceof ApiResponseError || err instanceof StepUpRequiredError) {
    return err.message;
  }
  return 'The request could not be completed. Check your connection and try again.';
}

/**
 * Log a client-side throw from a lifecycle mutation, whose own text the
 * dialog does not show. An HTTP failure is not logged: its message is on
 * screen.
 */
function logClientFailure(operation: PendingStepUp['operation'], err: unknown): void {
  if (err instanceof ApiResponseError || err instanceof StepUpRequiredError) return;
  console.error(`[staff-lifecycle] ${operation} failed`, err);
}

export function StaffDetailPage() {
  const navigate = useNavigate();
  const { staffId } = useParams<{ staffId: string }>();

  const staffQuery = useStaffMember(staffId);
  const suspendMutation = useSuspendStaff();
  const reactivateMutation = useReactivateStaff();
  const offboardMutation = useOffboardStaff();
  const resetCredentialMutation = useResetStaffCredential();
  const updateRolesMutation = useUpdateStaffRoles();
  const previewMutation = useEffectivePermissionsPreview();

  // Sensitive-operation orchestration. The flow is:
  //   1. Operator clicks an action.
  //   2. UI opens StepUpAuthModal to mint the operation-bound token.
  //   3. On verification the parent stashes the token and opens the
  //      destructive confirmation dialog.
  //   4. The confirmation handler invokes the mutation with the cached
  //      token. On success the dialog closes and the staff record refreshes.
  const [pendingStepUp, setPendingStepUp] = useState<PendingStepUp | null>(null);
  const [verifiedToken, setVerifiedToken] = useState<string | null>(null);
  const [activeAction, setActiveAction] = useState<
    'suspend' | 'offboard' | 'edit_roles' | 'reset_credential' | null
  >(null);

  // Recovery-window expiry from the last successful credential reset.
  // Surfaced as a success alert so the operator can tell the staff
  // member how long the emailed reset invitation stays valid.
  const [resetCredentialExpiresAt, setResetCredentialExpiresAt] = useState<
    string | null
  >(null);

  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [reactivateReason, setReactivateReason] = useState('');
  const [reactivateTouched, setReactivateTouched] = useState(false);

  const [editRolesOpen, setEditRolesOpen] = useState(false);
  const [proposedRoleIds, setProposedRoleIds] = useState<string[]>([]);
  const [editRolesReason, setEditRolesReason] = useState('');
  const [editRolesReasonTouched, setEditRolesReasonTouched] = useState(false);
  const [previewData, setPreviewData] =
    useState<PreviewEffectivePermissionsResponse | null>(null);
  const [editRolesServerError, setEditRolesServerError] =
    useState<EffectivePermissionsDiffServerError | null>(null);
  // The role selection is seeded from a staff record read when [Edit Roles]
  // opens (GH #2307, from the #2318 triage). A save sends the complete role
  // set and the server reconciles to it, so a seed from the cached record
  // would revoke a role granted since this page loaded. Until the read
  // lands, the picker, the preview and the save wait; a failed read blocks
  // the save. The sequence number drops a read that returns after its
  // dialog closed.
  const [editRolesSeed, setEditRolesSeed] = useState<'loading' | 'ready' | 'failed'>(
    'ready',
  );
  const editRolesSeedSeqRef = useRef(0);

  const staff: Staff | undefined = staffQuery.data;

  // The catalog is read only for a staff record whose roles can be edited,
  // so the ids are resolved before the operator finishes step-up.
  const roleCatalogQuery = useRoleCatalog({
    enabled: staff?.status === 'ACTIVE' || staff?.status === 'SUSPENDED',
  });
  const offeredRoles = useMemo(
    () => toAssignableStaffRoles(roleCatalogQuery.data ?? []),
    [roleCatalogQuery.data],
  );

  // When the staff record loads, seed the [Edit Roles] role-id selection
  // with the staff member's current role set so the diff renders empty
  // until the operator changes it.
  useEffect(() => {
    if (staff && proposedRoleIds.length === 0 && !editRolesOpen) {
      setProposedRoleIds(staff.roles.map((r) => r.id));
    }
    // intentionally narrow deps -- only re-seed on staff load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff?.id]);

  // StepUpAuthModal calls onVerified(token) and then onOpenChange(false) in
  // the same tick. Without this latch the modal's close handler runs
  // handleStepUpCancel, which clears the token that was just minted, and the
  // follow-up mutation goes out with stepUpToken undefined (a 403
  // STEP_UP_REQUIRED at the guard). The latch tells the close handler that
  // this close is a successful verification, not an operator cancel.
  const verificationSucceededRef = useRef(false);

  // The reactivate reason dialog opens after step-up, like the other
  // sensitive operations (fi-staff-lifecycle 1.2.0 declares X-Step-Up-Token
  // on reactivateStaff; the server has required it all along).
  const openReactivateDialog = () => {
    setReactivateReason('');
    setReactivateTouched(false);
    setReactivateOpen(true);
  };

  const closeReactivateDialog = () => {
    setReactivateOpen(false);
    setReactivateReason('');
    setReactivateTouched(false);
    setVerifiedToken(null);
    reactivateMutation.reset();
  };

  const openEditRolesDialog = () => {
    editRolesSeedSeqRef.current += 1;
    const seq = editRolesSeedSeqRef.current;
    setEditRolesSeed('loading');
    setEditRolesOpen(true);
    const seedFromServer = async () => {
      try {
        const result = await staffQuery.refetch();
        if (seq !== editRolesSeedSeqRef.current) return;
        // A failed refetch keeps the cached data on the result; never seed
        // from it.
        if (result.isError || !result.data) {
          setEditRolesSeed('failed');
          return;
        }
        setProposedRoleIds(result.data.roles.map((r) => r.id));
        setEditRolesSeed('ready');
      } catch (err) {
        if (seq !== editRolesSeedSeqRef.current) return;
        console.error('[staff-roles] current roles read failed', err);
        setEditRolesSeed('failed');
      }
    };
    void seedFromServer();
  };

  const handleStepUpVerified = (token: string) => {
    verificationSucceededRef.current = true;
    setVerifiedToken(token);
    if (pendingStepUp?.operation === 'suspend') {
      setActiveAction('suspend');
    } else if (pendingStepUp?.operation === 'reactivate') {
      openReactivateDialog();
    } else if (pendingStepUp?.operation === 'offboard') {
      setActiveAction('offboard');
    } else if (pendingStepUp?.operation === 'reset_credential') {
      setActiveAction('reset_credential');
    } else if (pendingStepUp?.operation === 'edit_roles') {
      openEditRolesDialog();
    }
    setPendingStepUp(null);
  };

  const handleStepUpCancel = () => {
    if (verificationSucceededRef.current) {
      // The modal is closing because verification SUCCEEDED; keep the token.
      verificationSucceededRef.current = false;
      return;
    }
    setPendingStepUp(null);
    setVerifiedToken(null);
  };

  const requestStepUp = (
    operation: PendingStepUp['operation'],
    label: string,
  ) => {
    if (!staff) return;
    verificationSucceededRef.current = false;
    const hash = buildStepUpHash(operation, staff.id);
    const cached = getCachedStepUpToken(hash);
    if (cached !== null) {
      setVerifiedToken(cached);
      if (operation === 'suspend') setActiveAction('suspend');
      else if (operation === 'reactivate') openReactivateDialog();
      else if (operation === 'offboard') setActiveAction('offboard');
      else if (operation === 'reset_credential') setActiveAction('reset_credential');
      else if (operation === 'edit_roles') openEditRolesDialog();
      return;
    }
    setPendingStepUp({ operation, label, hash });
  };

  const handleSuspendConfirm = async (reason: string) => {
    if (!staff) return;
    try {
      await suspendMutation.mutateAsync({
        userId: staff.id,
        body: { reason },
        stepUpToken: verifiedToken ?? undefined,
      });
      setActiveAction(null);
      setVerifiedToken(null);
    } catch (err) {
      // Error rendered inside the dialog (suspendMutation.error); the dialog
      // stays open so the operator can retry or cancel.
      logClientFailure('suspend', err);
    }
  };

  const handleOffboardConfirm = async (reason: string) => {
    if (!staff) return;
    try {
      await offboardMutation.mutateAsync({
        userId: staff.id,
        body: { reason },
        stepUpToken: verifiedToken ?? undefined,
      });
      setActiveAction(null);
      setVerifiedToken(null);
    } catch (err) {
      // Error rendered inside the dialog (offboardMutation.error).
      logClientFailure('offboard', err);
    }
  };

  const handleResetCredentialConfirm = async (reason: string) => {
    if (!staff) return;
    try {
      const result = await resetCredentialMutation.mutateAsync({
        userId: staff.id,
        body: { reason },
        stepUpToken: verifiedToken ?? undefined,
      });
      setResetCredentialExpiresAt(result.expiresAt);
      setActiveAction(null);
      setVerifiedToken(null);
    } catch (err) {
      // Error rendered inside the dialog (resetCredentialMutation.error);
      // the dialog stays open so the operator can retry or cancel.
      logClientFailure('reset_credential', err);
    }
  };

  const reactivateValid = reactivateReason.trim().length >= 10;

  const handleReactivate = async () => {
    if (!staff) return;
    setReactivateTouched(true);
    if (!reactivateValid || reactivateMutation.isPending) return;
    try {
      await reactivateMutation.mutateAsync({
        userId: staff.id,
        body: { reason: reactivateReason.trim() },
        stepUpToken: verifiedToken ?? undefined,
      });
      closeReactivateDialog();
    } catch (err) {
      // Error rendered inside the dialog (reactivateMutation.error).
      logClientFailure('reactivate', err);
    }
  };

  const editRolesReasonValid = editRolesReason.trim().length >= 10;

  // When the role selection or staff changes, refresh the preview so the
  // diff card reflects the operator's current proposal. The preview is a
  // POST mutation; we trigger it manually rather than through useQuery
  // because the body changes per click.
  useEffect(() => {
    if (!editRolesOpen || !staff || editRolesSeed !== 'ready') return;
    setEditRolesServerError(null);
    previewMutation.mutate(
      {
        userId: staff.id,
        body: { hypotheticalRoleIds: proposedRoleIds },
      },
      {
        onSuccess: (data) => setPreviewData(data),
      },
    );
    // intentionally narrow deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRolesOpen, editRolesSeed, proposedRoleIds, staff?.id]);

  const handleToggleRole = (roleId: string) => {
    setProposedRoleIds((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId],
    );
  };

  const handleEditRolesCommit = async () => {
    if (!staff) return;
    setEditRolesReasonTouched(true);
    if (
      !editRolesReasonValid ||
      updateRolesMutation.isPending ||
      editRolesSeed !== 'ready'
    ) {
      return;
    }
    setEditRolesServerError(null);
    try {
      await updateRolesMutation.mutateAsync({
        userId: staff.id,
        body: {
          roleIds: proposedRoleIds,
          reason: editRolesReason.trim(),
        },
        stepUpToken: verifiedToken ?? undefined,
      });
      setEditRolesOpen(false);
      setEditRolesReason('');
      setEditRolesReasonTouched(false);
      setPreviewData(null);
      setVerifiedToken(null);
    } catch (err) {
      // Surface 409 LAST_ADMIN_INVARIANT through EffectivePermissionsDiff.
      //
      // GH #2461 deliverable 3, sibling sweep. Two changes from the prior form:
      //
      // 1. Discriminate on the server's machine-readable code, not on prose.
      //    `staff-lifecycle.service.ts:1705` emits `LAST_ADMIN_INVARIANT` as the
      //    `error` field, and `ApiResponseError` now carries it. The previous
      //    `/last[_ ]?admin/i` test read the MESSAGE, so a reworded server
      //    string silently reclassified the failure as a generic UPDATE_FAILED
      //    and the operator lost the one explanation that tells them what to do.
      // 2. A client-side throw no longer renders its own text. This mutation is
      //    step-up gated, so it goes through the same middleware whose
      //    `request.clone()` TypeError once reached an operator as a
      //    verification message; a raw internal must not reach this panel either.
      if (!(err instanceof ApiResponseError)) {
        console.error('[staff-roles] update failed', err);
      }
      setEditRolesServerError(classifyRoleUpdateFailure(err));
    }
  };

  const handleEditRolesCancel = () => {
    if (updateRolesMutation.isPending) return;
    editRolesSeedSeqRef.current += 1;
    setEditRolesSeed('ready');
    setEditRolesOpen(false);
    setEditRolesReason('');
    setEditRolesReasonTouched(false);
    setEditRolesServerError(null);
    setPreviewData(null);
    if (staff) {
      setProposedRoleIds(staff.roles.map((r) => r.id));
    }
  };

  // ---- Render ----

  if (!staffId) {
    return (
      <div>
        <Alert type="error" title="Staff record not found">
          The URL is missing a staff identifier.
        </Alert>
      </div>
    );
  }

  if (staffQuery.isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div
          role="status"
          aria-label="Loading staff record"
          className="w-10 h-10 border-4 border-accent border-t-transparent rounded-full animate-spin"
        />
      </div>
    );
  }

  if (staffQuery.isError || !staff) {
    return (
      <div>
        <button
          type="button"
          onClick={() => void navigate('/admin/users')}
          className="mb-4 inline-flex items-center gap-1 text-sm font-sans text-tertiary hover:text-primary"
        >
          <BackIcon size={14} />
          Back to staff
        </button>
        <Alert type="error" title="Unable to load staff record">
          {staffQuery.error?.message ??
            'The staff record could not be loaded.'}
        </Alert>
      </div>
    );
  }

  const badge = STATUS_TO_BADGE[staff.status] ?? STATUS_TO_BADGE.INVITED;
  const canSuspend = staff.status === 'ACTIVE';
  const canReactivate = staff.status === 'SUSPENDED';
  const canOffboard = staff.status === 'ACTIVE' || staff.status === 'SUSPENDED';
  const canEditRoles =
    staff.status === 'ACTIVE' || staff.status === 'SUSPENDED';
  // Credential reset is offered only when the tenant authenticates staff
  // via Aaim-managed passwords (`credentialPathEnabled`) AND the staff
  // member is ACTIVE, the one state resetStaffCredential accepts; every
  // other status answers 409. An SSO-only tenant has no Aaim-managed
  // password, so the control never renders there. A SUSPENDED member is
  // reactivated first, then reset (GH #2307, Robert's ruling of
  // 2026-10-23, item 6 A): redeeming a reset link would otherwise set the
  // membership ACTIVE without the reactivate step-up.
  const canResetCredential =
    staff.credentialPathEnabled === true && staff.status === 'ACTIVE';

  // The diff's AFTER side: every proposed id the page can name. A role the
  // staff member already holds keeps its server RoleRef, so an unchanged role
  // the picker does not offer still reads as unchanged.
  const roleRefsById = new Map<string, RoleRef>();
  for (const role of offeredRoles) roleRefsById.set(role.id, role);
  for (const role of staff.roles) roleRefsById.set(role.id, role);
  const proposedRoles = proposedRoleIds.flatMap((id) => {
    const role = roleRefsById.get(id);
    return role ? [role] : [];
  });

  return (
    <div>
      <button
        type="button"
        onClick={() => void navigate('/admin/users')}
        className="mb-4 inline-flex items-center gap-1 text-sm font-sans text-tertiary hover:text-primary"
      >
        <BackIcon size={14} />
        Back to staff
      </button>

      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="min-w-0">
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <BodyText size="sm" color="tertiary" className="font-mono">
            {staff.email}
          </BodyText>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs font-sans text-tertiary">
            <Badge variant={badge.variant} size="small">
              {badge.label}
            </Badge>
            <span>
              Created{' '}
              <span className="text-secondary">{formatDate(staff.createdAt)}</span>
            </span>
            <span>
              Last login{' '}
              <span className="text-secondary">
                {formatDate(staff.lastLoginAt)}
              </span>
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {canEditRoles && (
            <Button
              variant="secondary"
              onClick={() =>
                requestStepUp(
                  'edit_roles',
                  `Edit roles for ${staff.displayName || staff.email}`,
                )
              }
            >
              <EditRolesIcon size={14} className="mr-2" />
              Edit roles
            </Button>
          )}
          {canSuspend && (
            <Button
              variant="secondary"
              onClick={() =>
                requestStepUp(
                  'suspend',
                  `Suspend ${staff.displayName || staff.email}`,
                )
              }
            >
              <SuspendIcon size={14} className="mr-2" />
              Suspend
            </Button>
          )}
          {canReactivate && (
            <Button
              variant="secondary"
              onClick={() =>
                requestStepUp(
                  'reactivate',
                  `Reactivate ${staff.displayName || staff.email}`,
                )
              }
            >
              <ReactivateIcon size={14} className="mr-2" />
              Reactivate
            </Button>
          )}
          {canResetCredential && (
            <Button
              variant="secondary"
              onClick={() =>
                requestStepUp(
                  'reset_credential',
                  `Reset sign-in credential for ${staff.displayName || staff.email}`,
                )
              }
            >
              <ResetCredentialIcon size={14} className="mr-2" />
              Reset sign-in credential
            </Button>
          )}
          {canOffboard && (
            <Button
              variant="primary"
              onClick={() =>
                requestStepUp(
                  'offboard',
                  `Off-board ${staff.displayName || staff.email}`,
                )
              }
            >
              <OffboardIcon size={14} className="mr-2" />
              Off-board
            </Button>
          )}
        </div>
      </div>

      {resetCredentialExpiresAt !== null && (
        <div className="mb-4">
          <Alert type="success" title="Sign-in credential reset">
            The password credential is deactivated and live sessions are
            revoked. A single-use reset invitation was emailed to{' '}
            {staff.email}; it expires{' '}
            {formatDate(resetCredentialExpiresAt)}. The reset is recorded on
            the staff member&apos;s audit trail.
          </Alert>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <div className="px-6 py-4 border-b border-recessed">
            <Heading level={2} size="subsection">
              Role bindings
            </Heading>
            <BodyText size="xs" color="tertiary" className="mt-1">
              Active (non-revoked) roles held at your institution.
            </BodyText>
          </div>
          <div className="p-4">
            {staff.roles.length === 0 ? (
              <p className="text-sm font-sans text-tertiary italic">
                No roles bound. Use [Edit roles] to grant access.
              </p>
            ) : (
              <ul className="space-y-2">
                {staff.roles.map((role) => (
                  <li
                    key={role.id}
                    className="flex items-baseline justify-between gap-3 px-3 py-2 border border-recessed rounded-sm"
                  >
                    <span className="font-sans font-semibold text-primary">
                      {role.name}
                    </span>
                    <span className="font-mono text-xs text-tertiary">
                      {role.slug}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Card>

        <Card>
          <div className="px-6 py-4 border-b border-recessed">
            <Heading level={2} size="subsection">
              Audit tail
            </Heading>
            <BodyText size="xs" color="tertiary" className="mt-1">
              Lifecycle and role events for this staff record, newest first.
            </BodyText>
          </div>
          <div className="p-4">
            <StaffAuditTail staffId={staff.id} />
          </div>
        </Card>
      </div>

      {/* Step-up modal -- gates Suspend, Reactivate, Reset sign-in
          credential, Off-board, and Edit roles. */}
      <StepUpAuthModal
        open={pendingStepUp !== null}
        onOpenChange={(open) => {
          if (!open) handleStepUpCancel();
        }}
        operation={
          pendingStepUp ? toCanonicalStepUpOperation(pendingStepUp.operation) : 'suspend'
        }
        subjectUserId={staff.id}
        operationLabel={pendingStepUp?.label ?? ''}
        operationHash={pendingStepUp?.hash}
        onVerified={handleStepUpVerified}
        onCancel={handleStepUpCancel}
      />

      {/* Suspend confirmation -- opens after step-up succeeds. */}
      <SuspendConfirmDialog
        open={activeAction === 'suspend'}
        onOpenChange={(open) => {
          if (!open) {
            setActiveAction(null);
            setVerifiedToken(null);
            suspendMutation.reset();
          }
        }}
        staffName={staff.displayName || staff.email}
        staffEmail={staff.email}
        onConfirm={handleSuspendConfirm}
        isPending={suspendMutation.isPending}
        errorMessage={lifecycleFailureMessage(suspendMutation.error)}
      />

      {/* Off-board confirmation -- two-step warning + email-verbatim. */}
      <OffboardConfirmDialog
        open={activeAction === 'offboard'}
        onOpenChange={(open) => {
          if (!open) {
            setActiveAction(null);
            setVerifiedToken(null);
            offboardMutation.reset();
          }
        }}
        staffName={staff.displayName || staff.email}
        staffEmail={staff.email}
        onConfirm={handleOffboardConfirm}
        isPending={offboardMutation.isPending}
        errorMessage={lifecycleFailureMessage(offboardMutation.error)}
      />

      {/* Credential reset confirmation -- opens after step-up succeeds. */}
      <ResetCredentialConfirmDialog
        open={activeAction === 'reset_credential'}
        onOpenChange={(open) => {
          if (!open) {
            setActiveAction(null);
            setVerifiedToken(null);
            resetCredentialMutation.reset();
          }
        }}
        staffName={staff.displayName || staff.email}
        staffEmail={staff.email}
        onConfirm={handleResetCredentialConfirm}
        isPending={resetCredentialMutation.isPending}
        errorMessage={lifecycleFailureMessage(resetCredentialMutation.error)}
      />

      {/* Reactivate dialog -- opens after step-up succeeds; reason >=10 chars. */}
      <Dialog
        open={reactivateOpen}
        onOpenChange={(open) => {
          if (!open && !reactivateMutation.isPending) closeReactivateDialog();
        }}
        title={`Reactivate ${staff.displayName || staff.email}`}
        size="md"
        isDismissable={!reactivateMutation.isPending}
        isKeyboardDismissDisabled={reactivateMutation.isPending}
        footer={
          <>
            <Button
              variant="tertiary"
              onClick={() => {
                if (reactivateMutation.isPending) return;
                closeReactivateDialog();
              }}
              disabled={reactivateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                void handleReactivate();
              }}
              disabled={!reactivateValid || reactivateMutation.isPending}
            >
              {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate'}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {reactivateMutation.isError && (
            <Alert type="error" title="Reactivate failed">
              {lifecycleFailureMessage(reactivateMutation.error)}
            </Alert>
          )}
          <BodyText size="sm" color="secondary">
            Reactivating restores the staff member&apos;s prior role
            bindings. Sessions remain revoked until the user signs in
            again.
          </BodyText>
          <div>
            <label
              htmlFor="reactivate-reason"
              className="block text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-2"
            >
              Reason
            </label>
            <textarea
              id="reactivate-reason"
              value={reactivateReason}
              onChange={(e) => {
                setReactivateReason(e.target.value);
                if (!reactivateTouched) setReactivateTouched(true);
              }}
              onBlur={() => setReactivateTouched(true)}
              rows={4}
              maxLength={500}
              placeholder="Explain the operational reason for reactivation."
              className="w-full px-3 py-2 border border-recessed rounded-sm font-sans text-sm bg-canvas text-primary focus:outline-none focus:border-accent"
            />
            <p className="mt-1 text-xs font-sans text-tertiary">
              Minimum 10 characters. Recorded on the staff member&apos;s
              audit trail.
            </p>
            {reactivateTouched && !reactivateValid && (
              <p className="mt-1 text-xs font-sans text-error">
                Provide at least 10 characters.
              </p>
            )}
          </div>
        </div>
      </Dialog>

      {/* Edit roles dialog -- opens after step-up succeeds. */}
      <Dialog
        open={editRolesOpen}
        onOpenChange={(open) => {
          if (!open) handleEditRolesCancel();
        }}
        title={`Edit roles for ${staff.displayName || staff.email}`}
        size="full"
        isDismissable={!updateRolesMutation.isPending}
        isKeyboardDismissDisabled={updateRolesMutation.isPending}
        footer={
          <>
            <Button
              variant="tertiary"
              onClick={handleEditRolesCancel}
              disabled={updateRolesMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                void handleEditRolesCommit();
              }}
              disabled={
                !editRolesReasonValid ||
                updateRolesMutation.isPending ||
                editRolesSeed !== 'ready'
              }
            >
              {updateRolesMutation.isPending
                ? 'Saving...'
                : 'Save role changes'}
            </Button>
          </>
        }
      >
        <div className="space-y-6">
          <div>
            <h3 className="text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-3">
              Proposed role set
            </h3>
            <div className="space-y-2">
              {roleCatalogQuery.isPending && (
                <p className="text-sm font-sans text-tertiary">
                  Loading role catalog...
                </p>
              )}
              {roleCatalogQuery.isError && roleCatalogQuery.data === undefined && (
                <Alert type="error" title="Role catalog unavailable">
                  The role catalog could not be loaded, so roles cannot be
                  granted or revoked here. Reload the page to try again.
                </Alert>
              )}
              {roleCatalogQuery.isSuccess && offeredRoles.length === 0 && (
                <p className="text-sm font-sans text-tertiary italic">
                  The role catalog returned no roles this institution can assign.
                </p>
              )}
              {editRolesSeed === 'loading' && (
                <p className="text-sm font-sans text-tertiary">
                  Reading the staff member&apos;s current roles...
                </p>
              )}
              {editRolesSeed === 'failed' && (
                <Alert type="error" title="Current roles unavailable">
                  The staff member&apos;s current roles could not be read,
                  so a save here could revoke a role granted since this page
                  loaded. Close this dialog and open it again to retry.
                </Alert>
              )}
              {editRolesSeed === 'ready' && offeredRoles.map((role) => {
                const checked = proposedRoleIds.includes(role.id);
                return (
                  <label
                    key={role.id}
                    className="flex items-baseline gap-3 px-3 py-2 border border-recessed rounded-sm cursor-pointer hover:bg-canvas-subtle"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleRole(role.id)}
                      className="mt-0.5"
                    />
                    <span className="font-sans font-semibold text-primary">
                      {role.name}
                    </span>
                    <span className="font-mono text-xs text-tertiary">
                      {role.slug}
                    </span>
                  </label>
                );
              })}
            </div>
            <p className="mt-2 text-xs font-sans text-tertiary">
              Granting fi_admin requires staff:grant_admin permission and
              the step-up token issued for this session.
            </p>
          </div>

          {previewMutation.isPending && !previewData && (
            <p className="text-sm font-sans text-tertiary">
              Computing effective permissions preview...
            </p>
          )}

          {previewData && (
            <EffectivePermissionsDiff
              beforePermissions={previewData.current.permissions}
              afterPermissions={previewData.proposed.permissions}
              beforeRoles={staff.roles}
              afterRoles={proposedRoles}
              beforeAbacPredicates={previewData.current.abacPredicates}
              afterAbacPredicates={previewData.proposed.abacPredicates}
              serverError={editRolesServerError ?? undefined}
            />
          )}

          {previewMutation.isError && !previewData && (
            <Alert type="error" title="Preview unavailable">
              {previewMutation.error?.message ??
                'The effective-permissions preview could not be computed.'}
            </Alert>
          )}

          {/*
            A failed save says so. The diff panel renders the last-admin
            refusal only, and only while a preview is on screen, so every
            other failure (a 500, a 403, a dropped connection) and a
            last-admin refusal with no preview left the dialog open and
            silent. The message is classifyRoleUpdateFailure's: the server's
            own text for an HTTP failure, fixed copy for a client-side throw.
          */}
          {editRolesServerError &&
            (editRolesServerError.code !== 'LAST_ADMIN_INVARIANT' || !previewData) && (
              <Alert type="error" title="Role update failed">
                {editRolesServerError.message}
              </Alert>
            )}

          <div>
            <label
              htmlFor="edit-roles-reason"
              className="block text-xs font-sans font-semibold uppercase tracking-[0.15em] text-tertiary mb-2"
            >
              Reason
            </label>
            <textarea
              id="edit-roles-reason"
              value={editRolesReason}
              onChange={(e) => {
                setEditRolesReason(e.target.value);
                if (!editRolesReasonTouched) setEditRolesReasonTouched(true);
              }}
              onBlur={() => setEditRolesReasonTouched(true)}
              rows={3}
              maxLength={500}
              placeholder="Explain the operational reason for the role change."
              className="w-full px-3 py-2 border border-recessed rounded-sm font-sans text-sm bg-canvas text-primary focus:outline-none focus:border-accent"
            />
            <p className="mt-1 text-xs font-sans text-tertiary">
              Minimum 10 characters. Recorded per-delta in audit events.
            </p>
            {editRolesReasonTouched && !editRolesReasonValid && (
              <p className="mt-1 text-xs font-sans text-error">
                Provide at least 10 characters.
              </p>
            )}
          </div>
        </div>
      </Dialog>
    </div>
  );
}

export default StaffDetailPage;
